/**
 * PasswordChangeForm — shared form for password change flows.
 *
 * Phase-3 W4.T6 extracted this from `force-password-change.page.tsx` so the
 * /profile self-service tab and the /force-password-change interstitial both
 * render identical chrome (validation, submit-state, 403/422 handling) without
 * duplicating the schema or the mutation glue.
 *
 * Usage:
 *
 *   <PasswordChangeForm
 *     mode="self"
 *     requireOldPassword
 *     submitLabel="Изменить пароль"
 *     onSuccess={() => …}
 *   />
 *
 * Props:
 *   - `mode`              — 'force' | 'self'. Affects test-id naming so
 *                           force-password-change and profile tests can
 *                           coexist without collisions, and routes the
 *                           success toast copy.
 *   - `requireOldPassword` — when true, renders + validates the «Текущий
 *                           пароль» field. The shared `<PasswordChangeForm>`
 *                           always POSTs `currentPassword` to the BE; when
 *                           the field is hidden, an empty string is sent
 *                           and the BE rejects with 403 (the FE never
 *                           lands here for /force-password-change today —
 *                           the temp pw IS the current pw — but the prop
 *                           lets callers opt-in to a future first-login
 *                           BE path that doesn't require the old pw).
 *   - `onSuccess`         — fired after a successful POST + cache
 *                           invalidation, before any caller-side navigation.
 *
 * Error handling stays consistent with the prior page-level implementation
 * (Wave-5 fix 5.x.2 / devil F6): 403 WRONG_CURRENT_PASSWORD is surfaced
 * inline on the `currentPassword` field — NOT as a toast — so the fetcher's
 * auto-refresh path is never entered and the user stays logged in.
 */
import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ChangePasswordRequestSchema } from '@aris/shared';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/password-input';
import { toast } from '@/components/ui/toast';
import { ApiError, apiFetch } from '@/lib/fetcher';
import { qk } from '@/lib/query-keys';

export type PasswordChangeMode = 'force' | 'self';

export interface PasswordChangeFormProps {
  /** Drives copy + test-id namespace. Defaults to 'self'. */
  mode?: PasswordChangeMode;
  /** When true, the form requires + sends the current password. */
  requireOldPassword: boolean;
  /** Submit button copy. Defaults derive from `mode`. */
  submitLabel?: string;
  /** Fired after a successful submit + cache invalidation. */
  onSuccess?: () => void | Promise<void>;
  /** Auto-focus the first field on mount (force-pwd page wants this). */
  autoFocusFirstField?: boolean;
}

/**
 * Schema is built per render so the `currentPassword` constraint can be
 * relaxed when the field is hidden. We extend the shared
 * `ChangePasswordRequestSchema` (which already enforces the password
 * policy on `newPassword`) with a confirm field that must match.
 */
function buildSchema(requireOld: boolean) {
  const base = requireOld
    ? ChangePasswordRequestSchema
    : ChangePasswordRequestSchema.extend({
        // When the UI does NOT collect the current password we still need
        // a string in the body shape; an empty string is valid here, but
        // the BE will reject. This branch is reserved for a future first-
        // login flow.
        currentPassword: z.string().max(256),
      });
  return base
    .extend({ confirmPassword: z.string().min(1, 'Подтвердите пароль') })
    .refine((v) => v.newPassword === v.confirmPassword, {
      message: 'Пароли не совпадают',
      path: ['confirmPassword'],
    });
}

export function PasswordChangeForm({
  mode = 'self',
  requireOldPassword,
  submitLabel,
  onSuccess,
  autoFocusFirstField = false,
}: PasswordChangeFormProps): React.ReactElement {
  const qc = useQueryClient();
  const [submitting, setSubmitting] = React.useState(false);

  const FormSchema = React.useMemo(() => buildSchema(requireOldPassword), [requireOldPassword]);
  type FormValues = z.infer<typeof FormSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' } as FormValues,
    mode: 'onSubmit',
  });

  const testIdPrefix = mode === 'force' ? 'force-pwd' : 'change-password';
  const buttonLabel = submitLabel ?? (mode === 'force' ? 'Сменить пароль' : 'Изменить пароль');
  const successToast = mode === 'force' ? 'Пароль обновлён' : 'Пароль изменён';

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await apiFetch('/users/me/change-password', {
        method: 'POST',
        body: {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
      });
      toast(successToast, { variant: 'success' });
      // Wave-5 fix 5.x.9 / devil F5: refetch (not just invalidate) so the
      // cache is GUARANTEED to reflect mustChangePassword=false BEFORE the
      // caller navigates. Same rationale as the prior page-level impl.
      await qc.refetchQueries({ queryKey: qk.auth.session() });
      form.reset();
      if (onSuccess) await onSuccess();
    } catch (err) {
      // Wave-5 fix 5.x.2: BE returns 403 WRONG_CURRENT_PASSWORD. 403 keeps
      // the user logged in (no auto-refresh path) and we surface inline
      // on the field — no toast.
      if (err instanceof ApiError && err.status === 403 && err.code === 'WRONG_CURRENT_PASSWORD') {
        // `currentPassword` is always part of the form even when hidden.
        form.setError('currentPassword' as never, { message: 'Неверный пароль' });
        return;
      }
      if (err instanceof ApiError && err.status === 422) {
        toast(err.message || 'Не удалось сменить пароль', { variant: 'error' });
        return;
      }
      toast('Не удалось сменить пароль', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        {requireOldPassword ? (
          <FormField
            name={'currentPassword' as never}
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Текущий пароль</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="current-password"
                    autoFocus={autoFocusFirstField}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <FormField
          name={'newPassword' as never}
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Новый пароль</FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="new-password"
                  autoFocus={autoFocusFirstField && !requireOldPassword}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name={'confirmPassword' as never}
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Подтверждение</FormLabel>
              <FormControl>
                <PasswordInput autoComplete="new-password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={submitting} data-testid={`${testIdPrefix}-submit`}>
          {submitting ? 'Сохранение…' : buttonLabel}
        </Button>
      </form>
    </Form>
  );
}
